// game/client/src/day-night-cycle.ts
//
// Pure cycle math shared by the lighting lerp (day-night-visual.ts) and the
// HUD clock cue (day-night-clock.ts). No three.js, no DOM — just maps the
// server-synced (phase, cycleElapsed) pair onto a continuous "nightness"
// factor for lighting and a discrete phase descriptor for the HUD.
//
// Server model (creatures/day-night.ts): phase flips INSTANTANEOUSLY at the
// boundary and resets cycleElapsed to 0. The 20s dusk/dawn blend is a
// client-only visual that runs entirely in the TAIL of the outgoing phase:
// the last 20s of day ramp toward full night (dusk), the last 20s of night ramp
// toward full day (dawn). Each phase therefore arrives at the boundary already
// at the next phase's value, so the curve is continuous across the flip — AND a
// fresh phase at cycleElapsed=0 reads as the full phase value, so the match
// opens at full daylight (no twilight-at-spawn artifact).

import { DAY_DURATION_SEC, NIGHT_DURATION_SEC, DAY_NIGHT_TRANSITION_SEC } from "@tribez/shared";

const BAND = DAY_NIGHT_TRANSITION_SEC; // full dusk/dawn ramp length, in the phase's tail

/** Countdown styling threshold — the next-transition timer turns "urgent" inside this window. */
export const URGENT_SEC = 45;

function durationOf(phase: number): number {
  return phase === 0 ? DAY_DURATION_SEC : NIGHT_DURATION_SEC;
}

/**
 * Continuous nightness for the lighting lerp.
 *   0 = full day, 1 = full night.
 * Full day/night through the body of each phase; the final BAND seconds ramp
 * toward the next phase. day-end → 1 and night-start → 1 (continuous); night-end
 * → 0 and day-start → 0 (continuous); match start (day, cycleElapsed 0) → 0.
 */
export function nightness(phase: number, cycleElapsed: number): number {
  const dur = durationOf(phase);
  const e = Math.max(0, Math.min(cycleElapsed, dur));
  const rampStart = dur - BAND;
  if (phase === 0) {
    // Day: full daylight, then ramp 0 → 1 over the final BAND secs (dusk).
    return e <= rampStart ? 0 : (e - rampStart) / BAND;
  }
  // Night: full night, then ramp 1 → 0 over the final BAND secs (dawn).
  return e <= rampStart ? 1 : 1 - (e - rampStart) / BAND;
}

/**
 * Twilight warmth 0..1 — how strongly the warm/red DUSK keyframe is blended OVER
 * the day↔night base in the lighting lerp (day-night-visual.ts). Distinct from
 * `nightness`, which only gates the night DARKNESS in the 20s tail: this is a
 * BROAD golden arc so the Tartakovsky warm-red look occupies much of the day, not
 * just the brief transition (Aviv 2026-06-24: "extend the dusk/dawn feel to more
 * of the day").
 *   - DAY: full warm-red at the dawn & dusk edges, easing to warm-gold at midday
 *     (U-shape over the day phase).
 *   - NIGHT: cool, with the red building over the last quarter (dawn) plus a short
 *     post-dusk afterglow at the start. Continuous across both phase flips
 *     (day-end→1 meets night-start→1; night-end→1 meets day-start→1).
 * Tuning: lower DAY_WARM_POWER spreads the red wider into midday; higher keeps
 * midday gold for longer.
 */
const DAY_WARM_POWER = 1.2;
export function twilightWarmth(phase: number, cycleElapsed: number): number {
  const dur = durationOf(phase);
  const frac = dur > 0 ? Math.max(0, Math.min(cycleElapsed, dur)) / dur : 0;
  if (phase === 0) {
    const fromMid = Math.abs(frac - 0.5) * 2; // 0 at midday, 1 at the day edges
    return Math.pow(fromMid, DAY_WARM_POWER);
  }
  const dawn = Math.max(0, (frac - 0.75) / 0.25);      // red builds over the last 1/4 of night
  const afterglow = Math.max(0, (0.15 - frac) / 0.15); // brief post-dusk glow at night start
  return Math.max(dawn, afterglow);
}

export interface PhaseInfo {
  /** CSS phase key → drives the clock's --phase-color theme. */
  key: "day" | "dusk" | "night" | "dawn";
  icon: string;
  /** "Day N" / "Night N". */
  label: string;
  /** Name of the next phase boundary the countdown is ticking toward. */
  nextLabel: "Dusk" | "Dawn";
  /** Seconds until that boundary. */
  secsToTransition: number;
  /** 0..1 progress through the current phase — drives the progress bar width. */
  phaseFraction: number;
  /** Stylised 24h in-world clock string ("HH:MM"), aligned to day=06–18, night=18–06. */
  clock: string;
}

export function phaseInfo(phase: number, cycleElapsed: number, dayCount: number): PhaseInfo {
  const dur = durationOf(phase);
  const e = Math.max(0, Math.min(cycleElapsed, dur));
  const frac = dur > 0 ? e / dur : 0;
  const toTransition = Math.max(0, dur - e);
  // The final BAND secs read as the upcoming transition (dusk/dawn) — the clock
  // theme shifts in lockstep with the visible sky ramp.
  const inTail = toTransition < BAND;

  if (phase === 0) {
    return {
      key: inTail ? "dusk" : "day",
      icon: "☀", // ☀
      label: `Day ${dayCount}`,
      nextLabel: "Dusk",
      secsToTransition: toTransition,
      phaseFraction: frac,
      // Day spans 06:00 → 18:00.
      clock: hhmm(6 + frac * 12),
    };
  }
  return {
    key: inTail ? "dawn" : "night",
    icon: "☾", // ☾
    label: `Night ${dayCount}`,
    nextLabel: "Dawn",
    secsToTransition: toTransition,
    phaseFraction: frac,
    // Night spans 18:00 → 06:00 (wraps past midnight).
    clock: hhmm(18 + frac * 12),
  };
}

function hhmm(hoursFloat: number): string {
  const total = Math.floor(((hoursFloat % 24) + 24) % 24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}
